import { CommonModule } from '@angular/common';
import type { OnDestroy, OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Output,
  ViewChild,
} from '@angular/core';
import type { Timer } from '@freelancer/time-utils';
import { TimeUtils } from '@freelancer/time-utils';
import { UiModule } from '@freelancer/ui';
import { Assets } from '@freelancer/ui/assets';
import { IconSize } from '@freelancer/ui/icon';
import { PictureDisplay } from '@freelancer/ui/picture';
import {
  FontColor,
  FontWeight,
  TextAlign,
  TextSize,
} from '@freelancer/ui/text';
import { VideoComponent, VideoObjectFit } from '@freelancer/ui/video';

@Component({
  standalone: true,
  imports: [CommonModule, UiModule],
  selector: 'app-ai-consultant-toggle',
  template: `
    <div
      *ngIf="shouldShowCallout"
      class="Callout"
      [flHideDesktop]="true"
    >
      <div class="Callout-content">
        <fl-text
          class="Callout-text"
          i18n="Callout content for the AI consultant toggle"
          [textAlign]="TextAlign.CENTER"
          [size]="TextSize.XXSMALL"
          [flMarginRight]="'xxsmall'"
        >
          Want personalised advice on <br />
          supercharging your business?
        </fl-text>
        <fl-icon
          class="Callout-close"
          flTrackingLabel="CloseCallout"
          [name]="'ui-close'"
          [size]="IconSize.XSMALL"
          (click)="closeCallout()"
        ></fl-icon>
      </div>
      <div class="Callout-arrow"></div>
    </div>

    <fl-button
      class="WidgetToggle"
      flTrackingLabel="ToggleConsultantWidget"
      (click)="toggleWidget()"
    >
      <div class="WidgetToggle-container">
        <fl-picture
          class="WidgetToggle-image"
          alt="AI Agent Avatar"
          i18n-alt="AI Agent Avatar"
          [src]="aiAvatarSrc"
          [height]="64"
          [width]="64"
          [display]="PictureDisplay.BLOCK"
        ></fl-picture>

        <div
          [flShowDesktop]="true"
          (mouseenter)="handleOnHoverIn()"
          (mouseleave)="handleOnHoverOut()"
        >
          <fl-text
            class="WidgetToggle-title"
            i18n="AI Consultant Widget toggle desktop title"
            [flShowDesktop]="true"
            [color]="FontColor.LIGHT"
            [size]="TextSize.SMALL"
            [weight]="FontWeight.BOLD"
          >
            Get advice on supercharging your business
          </fl-text>

          <fl-video
            #video
            class="WidgetToggle-lightning"
            [flHide]="!showVideo"
            [disableControls]="true"
            [src]="videoSrc"
            [objectFit]="VideoObjectFit.COVER"
            (videoEnd)="setVideoTimer()"
            (canPlayThrough)="playVideo()"
          ></fl-video>
        </div>
      </div>
    </fl-button>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./ai-consultant-toggle.component.scss'],
})
export class AiConsultantToggleComponent implements OnInit, OnDestroy {
  FontColor = FontColor;
  FontWeight = FontWeight;
  IconSize = IconSize;
  PictureDisplay = PictureDisplay;
  TextAlign = TextAlign;
  TextSize = TextSize;
  VideoObjectFit = VideoObjectFit;

  aiAvatarSrc = 'ai-consultant/ai-agent-avatar.jpg';
  videoSrc: string;
  videoTimer: Timer;
  showVideo = false;
  shouldShowCallout = false;

  @ViewChild('video') video: VideoComponent;
  @Output() onClick = new EventEmitter<void>();

  constructor(
    private assets: Assets,
    private cd: ChangeDetectorRef,
    private timeUtils: TimeUtils,
  ) {}

  ngOnInit(): void {
    this.videoSrc = this.assets.getUrl('ai-consultant/lightning.mp4');
  }

  playVideo(): void {
    this.showVideo = true;
    clearTimeout(this.videoTimer);
    this.video.play();
  }

  setVideoTimer(): void {
    if (this.showVideo) {
      this.showVideo = false;
      clearTimeout(this.videoTimer);
      this.videoTimer = this.timeUtils.setTimeout(() => {
        this.showVideo = true;
        this.video.play();
      }, 60_000);
    }
  }

  handleOnHoverIn(): void {
    this.video.pause();
    this.showVideo = false;
    clearTimeout(this.videoTimer);
  }

  handleOnHoverOut(): void {
    this.showVideo = true;
    this.setVideoTimer();
  }

  openCallout(): void {
    this.shouldShowCallout = true;
    this.cd.markForCheck();
  }

  closeCallout(): void {
    this.shouldShowCallout = false;
    this.cd.markForCheck();
  }

  toggleWidget(): void {
    this.closeCallout();
    this.onClick.emit();
  }

  ngOnDestroy(): void {
    clearTimeout(this.videoTimer);
  }
}
